import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import Head from 'next/head';
import CarCard from '../components/CarCard';
import A11yImage from '../components/A11yImage';
import { useRouter } from 'next/router';
import SEO from '../components/SEO';
import { getAllCars } from '../lib/shopify.mjs';
import { isEvCar } from '../lib/evFilter.js';
import { readCarStatuses } from '../lib/carStatusStore.js';
import { getCachedStatuses, setCachedStatuses } from '../lib/carStatusCache';
import { scheduleAfterLoadThenIdle } from '../utils/scheduler';
import { mergeCarSpecs } from '../lib/mergeCarSpecs';
import { useInView } from 'react-intersection-observer';

async function safeFetchJson(url, fetchOptions = {}, timeoutMs = 8000) {
  let timeoutId;
  const controller = new AbortController();
  try {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const resp = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });

    let data;
    try {
      data = await resp.json();
    } catch {
      data = null;
    }

    return { ok: resp.ok, status: resp.status, data };
  } catch (error) {
    return { ok: false, status: 0, data: null, error };
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

function normalizeQueryString(input, maxLen) {
  return String(input || '')
    .trim()
    .slice(0, maxLen);
}

function normalizePriceRange(input) {
  const value = String(input || 'all');
  if (value === 'all') return 'all';
  // allow forms like 100000-200000 or 700000
  if (/^\d+(?:-\d+)?$/.test(value)) return value;
  return 'all';
}

function normalizeBrand(input) {
  const value = normalizeQueryString(input, 40);
  return value ? value : 'all';
}

function normalizePageNumber(input) {
  const pg = parseInt(String(input || '1'), 10);
  return Number.isFinite(pg) && pg > 0 ? pg : 1;
}

export default function AllCars({
  cars,
  totalCount,
  totalPages,
  initialSearchTerm,
  initialPriceRange,
  initialBrandFilter,
  initialPage,
  structuredDataJson,
  shopifyError,
  seoAllCars,
  allCarsFaqs,
}) {
  const router = useRouter();
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [priceRange, setPriceRange] = useState(initialPriceRange);
  const [brandFilter, setBrandFilter] = useState(initialBrandFilter);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [liveStatuses, setLiveStatuses] = useState(null);
  const [showAllCars, setShowAllCars] = useState(true);
  const { ref: observerRef, inView } = useInView({
    triggerOnce: true,
    rootMargin: '400px 0px',
  });

  // Render all cars immediately to ensure SEO crawlability (Googlebot sees all 24 links)
  // We keep the state for compatibility but default to true.

  // useEffect(() => {
  //   if (inView) {
  //     setShowAllCars(true);
  //   }
  // }, [inView]);

  // useEffect(() => {
  //   // Fallback
  //   const timer = setTimeout(() => setShowAllCars(true), 2000);
  //   return () => clearTimeout(timer);
  // }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (process.env.NODE_ENV !== 'development') return;

    const onUnhandled = event => {
      const reason = event?.reason;
      const message =
        typeof reason === 'string'
          ? reason
          : typeof reason?.message === 'string'
            ? reason.message
            : '';

      if (
        message.includes('Failed to fetch') ||
        message.includes('NetworkError when attempting to fetch resource')
      ) {
        event.preventDefault();
      }
    };

    window.addEventListener('unhandledrejection', onUnhandled);
    return () => window.removeEventListener('unhandledrejection', onUnhandled);
  }, []);

  // Keep state in sync when user lands via router navigation (client-side) to a URL with params
  useEffect(() => {
    if (!router?.isReady) return;
    const q = router.query || {};
    const nextSearch = q.search ? normalizeQueryString(q.search, 120) : '';
    const nextPrice = q.price ? normalizePriceRange(q.price) : 'all';
    const nextBrand = q.brand ? normalizeBrand(q.brand) : 'all';
    const nextPage = q.page ? normalizePageNumber(q.page) : 1;

    setSearchTerm(prev => (prev === nextSearch ? prev : nextSearch));
    setPriceRange(prev => (prev === nextPrice ? prev : nextPrice));
    setBrandFilter(prev => (prev === nextBrand ? prev : nextBrand));
    setCurrentPage(prev => (prev === nextPage ? prev : nextPage));
  }, [router?.isReady, router?.query]);

  // SSR already provides only current page cars + totalCount/totalPages.
  // Keep client work minimal to reduce hydration cost (TBT).
  const safeTotalPages = Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1;
  const safePage = Math.min(Math.max(1, currentPage), safeTotalPages);
  const currentCars = useMemo(() => {
    const list = Array.isArray(cars) ? cars : [];
    return list.map(car => mergeCarSpecs(car, null));
  }, [cars]);

  const currentIds = useMemo(() => currentCars.map(c => c.id).filter(Boolean), [currentCars]);

  // Fetch latest statuses for current page cars on mount and page/filter change (with cache)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!currentIds || currentIds.length === 0) return;

    let cancelled = false;

    // Defer non-critical fetch to reduce main-thread contention during initial render.
    let cleanup = () => {};

    // Apply cached statuses immediately (no network / minimal work)
    try {
      const cached = getCachedStatuses(currentIds);
      if (cached) {
        if (!cancelled) setLiveStatuses(cached);
      }
    } catch {
      // ignore
    }

    const fetchStatuses = async () => {
      try {
        // Check cache first
        const cached = getCachedStatuses(currentIds);
        if (cached) return;

        const qs = new URLSearchParams({ ids: currentIds.join(',') });
        const result = await safeFetchJson(
          `/api/public/car-status?${qs.toString()}`,
          {
            cache: 'no-store',
            credentials: 'same-origin',
          },
          6000
        );

        if (!result.ok) {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.warn(
              `car-status fetch failed (status=${result.status || 'n/a'})`,
              result?.error?.message || ''
            );
          }
          return;
        }

        const data = result.data;
        if (!cancelled && data?.ok && data.statuses) {
          setCachedStatuses(data.statuses);
          setLiveStatuses(data.statuses);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('Failed to fetch car statuses:', error?.message);
        }
        // Silently fail - don't break the page on fetch error
      }
    };

    // Only run network fetch after the page load event and when the browser is idle.
    // This reduces TBT and avoids Lighthouse observing in-flight API calls during page load.
    cleanup = scheduleAfterLoadThenIdle(
      () => {
        fetchStatuses().catch(() => {});
      },
      { timeout: 5000, fallbackDelayMs: 3500 }
    );

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [currentIds]);

  // Enrich specs for current page cards by calling the same API as the car detail page.
  // For stability on paginated pages (2+), allow limited retries when a handle returns
  // empty/incomplete specs (e.g. transient Admin fallback failures).
  // Removed heavy car-specs polling fetch loop for performance.

  // Determine if current view is filtered (query params affecting list)
  // Free-text search creates unbounded, low-value URL variations -> noindex + canonicalize to base catalog.
  // Brand/price filters are a finite, high-value set of long-tail landing pages -> keep them indexable
  // with their own canonical URL (each combo gets a unique title/description via getDynamicSEO() below).
  const isFiltered = useMemo(() => {
    const hasSearch = !!(searchTerm && String(searchTerm).trim());
    return hasSearch; // Only free-text search is noindexed; brand/price combos stay indexable
  }, [searchTerm]);

  // Canonical URL for SEO component
  const seoPath = useMemo(() => {
    // Search queries are unbounded and can create infinite thin-content variations -> canonicalize to base catalog.
    if (isFiltered) return '/all-cars';
    // Preserve brand/price/page state so each combination is indexed as its own landing page.
    const params = new URLSearchParams();
    if (priceRange !== 'all') params.set('price', priceRange);
    if (brandFilter !== 'all') params.set('brand', brandFilter);
    if (safePage > 1) params.set('page', String(safePage));
    const qs = params.toString();
    return qs ? `/all-cars?${qs}` : '/all-cars';
  }, [isFiltered, priceRange, brandFilter, safePage]);

  // ฟังก์ชันสำหรับสร้างลิงก์ SEO-friendly
  const getPageUrl = page => {
    const params = new URLSearchParams();
    const term = String(searchTerm || '')
      .trim()
      .slice(0, 120);
    if (term) params.set('search', term);
    if (priceRange !== 'all') params.set('price', priceRange);
    if (brandFilter !== 'all') params.set('brand', brandFilter);
    if (page > 1) params.set('page', page.toString());

    const queryString = params.toString();
    return queryString ? `/all-cars?${queryString}` : '/all-cars';
  };

  // เปลี่ยนหน้าและเลื่อนไปที่จุดเริ่มต้นของรายการรถ (ไม่เด้งขึ้นไปบนสุดที่แบนเนอร์)
  const handlePaginationNav = (page, e) => {
    e.preventDefault();
    if (page === currentPage) return;

    // เลื่อนหน้าไป (scroll: false ปิดการเด้งไปบนสุดอัตโนมัติของ Next.js)
    router
      .push(getPageUrl(page), undefined, {
        scroll: false,
      })
      .then(() => {
        // ใช้ requestAnimationFrame หรือ setTimeout เพื่อดักให้ DOM เรนเดอร์รถชุดใหม่เสร็จก่อน
        // ถึงจะคำนวณตำแหน่งและเลื่อนจอจริงๆ
        setTimeout(() => {
          // เลื่อนไปที่ตะกร้ารถจริงๆ โดยใช้ scrollIntoView เพื่อไม่ให้เกิด forced reflow
          const grid = document.getElementById('cars-list-top');
          if (grid) {
            grid.scrollIntoView({ block: 'start', behavior: 'instant' });
          }
        }, 50);
      });
  };

  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5; // แสดงหน้าสูงสุด 5 หน้า

    let startPage = Math.max(1, safePage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // ปรับให้แสดงครบ 5 หน้าถ้าเป็นไปได้
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  // เริ่มการเรนเดอร์หน้า

  return (
    <div className="min-h-screen">
      {(() => {
        const getDynamicSEO = () => {
          let title = seoAllCars.titleBase;
          let isSpecific = false;
          const priceLabels = {
            '0-100000': 'ราคาไม่เกิน 1 แสน',
            '100000-200000': 'ราคา 1-2 แสน',
            '200000-300000': 'ราคา 2-3 แสน',
            '300000-400000': 'ราคา 3-4 แสน',
            '400000-500000': 'ราคา 4-5 แสน',
            '500000-600000': 'ราคา 5-6 แสน',
            '600000-700000': 'ราคา 6-7 แสน',
            700000: 'ราคา 7 แสนขึ้นไป',
          };
          const brandLabels = {
            toyota: 'รถ Toyota',
            honda: 'รถ Honda',
            nissan: 'รถ Nissan',
            mazda: 'รถ Mazda',
            mitsubishi: 'รถ Mitsubishi',
            isuzu: 'รถ Isuzu',
            ford: 'รถ Ford',
          };
          let prefix = [];
          if (brandFilter && brandFilter !== 'all' && brandLabels[brandFilter]) {
            prefix.push(brandLabels[brandFilter]);
            isSpecific = true;
          } else {
            prefix.push('รถมือสอง');
          }

          if (priceRange && priceRange !== 'all' && priceLabels[priceRange]) {
            prefix.push(priceLabels[priceRange]);
            isSpecific = true;
          }

          let desc =
            prefix.join(' ') +
            ' ศูนย์รวมรถบ้านมือเดียวในจังหวัดเชียงใหม่ สภาพดี ฟรีดาวน์ พร้อมรับประกัน 1 ปี';
          title = prefix.join(' ') + ' เชียงใหม่ สภาพดี ฟรีดาวน์';

          return { title, desc, isSpecific };
        };
        const dynamicSEO = getDynamicSEO();

        return (
          <SEO
            title={
              dynamicSEO.isSpecific
                ? `${dynamicSEO.title}${safeTotalPages > 1 && safePage > 1 ? ` หน้า ${safePage}` : ''} | ครูหนึ่งรถสวย`
                : `${seoAllCars.titleBase}${safeTotalPages > 1 && safePage > 1 ? ` หน้า ${safePage}` : ''} | ครูหนึ่งรถสวย`
            }
            description={
              dynamicSEO.isSpecific
                ? dynamicSEO.desc + ' โทร 094-064-9018'
                : `ดูรถยนต์มือสองคุณภาพดีทั้งหมด ${Number.isFinite(totalCount) ? totalCount : 0} คัน ในเชียงใหม่และภาคเหนือ คัดสรรทุกคัน ฟรีดาวน์ 0% รับประกัน 1 ปี ส่งฟรีทั่วไทย Toyota Honda Nissan Mazda นัดหมายดูรถโทร 094-064-9018`
            }
            keywords={[
              seoAllCars.primary,
              ...seoAllCars.secondary,
              ...seoAllCars.longTail.slice(0, 5),
            ]}
            url={seoPath}
            image={`https://www.chiangmaiusedcar.com/api/og?src=${encodeURIComponent(
              '/herobanner/heroallcars-1400w.webp'
            )}&w=1200&h=630`}
            type="website"
            pageType="all-cars"
            noindex={isFiltered}
            breadcrumbs={[
              { name: 'หน้าแรก', url: '/' },
              { name: 'รถมือสองทั้งหมด', url: '/all-cars' },
            ]}
            structuredData={structuredDataJson || null}
          />
        );
      })()}

      {process.env.NODE_ENV === 'development' && shopifyError && currentCars.length === 0 && (
        <section className="max-w-7xl mx-auto px-6 mt-4" aria-label="Dev Shopify error">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 font-prompt">
            <div className="font-bold text-red-900">Dev: ดึงข้อมูลรถจาก Shopify ไม่สำเร็จ</div>
            <div className="mt-1 text-sm text-red-800">
              ตรวจสอบไฟล์ <span className="font-semibold">.env.local</span> ว่ามี{' '}
              <span className="font-semibold">SHOPIFY_DOMAIN</span> และ{' '}
              <span className="font-semibold">SHOPIFY_STOREFRONT_TOKEN</span> แล้วรันใหม่ด้วย{' '}
              <span className="font-semibold">pnpm dev</span>
            </div>
            <div className="mt-1 text-xs text-red-700/80">รายละเอียด: {shopifyError}</div>
          </div>
        </section>
      )}

      {/* Pagination Link Tags for SEO */}
      {totalPages > 1 && !isFiltered && (
        <Head>
          {safePage > 1 && (
            <link rel="prev" href={`https://www.chiangmaiusedcar.com${getPageUrl(safePage - 1)}`} />
          )}
          {safePage < totalPages && (
            <link rel="next" href={`https://www.chiangmaiusedcar.com${getPageUrl(safePage + 1)}`} />
          )}
        </Head>
      )}

      {/* Hero Banner - 2025 Modern Design */}
      <section
        className="relative w-full bg-[#f8f9fa] flex justify-center"
        aria-label="รถมือสองทั้งหมด - ครูหนึ่งรถสวย"
      >
        {/* Container สำหรับรูปภาพที่ปรับอัตราส่วนตามภาพจริง (1400/474) ในทุกอุปกรณ์ */}
        <div className="relative w-full max-w-[1400px] aspect-[1400/474]">
          <div className="absolute inset-0" aria-hidden="true">
            <A11yImage
              src="/herobanner/heroallcars-1400w.webp"
              customSrcSet="/herobanner/heroallcars-414w.webp 414w, /herobanner/heroallcars-640w.webp 640w, /herobanner/heroallcars-828w.webp 828w, /herobanner/heroallcars-1024w.webp 1024w, /herobanner/heroallcars-1400w.webp 1400w"
              customSizes="100vw"
              alt="รวมรถยนต์มือสองคุณภาพดี ครูหนึ่งรถสวย เชียงใหม่"
              className="absolute inset-0 w-full h-full object-cover object-top"
              fetchPriority="high"
              priority
              decoding="sync"
              fill
              imageType="hero"
              optimizeImage={false}
            />
          </div>

          {/* Dark overlay for better text readability (mobile + desktop) */}
          <div className="absolute inset-0 bg-black/35 sm:bg-black/40"></div>

          {/* Content over banner */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4 max-w-4xl mx-auto">
              <h1
                className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-1 md:mb-2 font-prompt text-white mt-0 md:mt-4"
                style={{
                  textShadow:
                    '2px 2px 4px rgba(0,0,0,0.8), -1px -1px 2px rgba(0,0,0,0.8), 1px -1px 2px rgba(0,0,0,0.8), -1px 1px 2px rgba(0,0,0,0.8)',
                }}
              >
                {seoAllCars.h1 || 'รวมรถมือสองเชียงใหม่ ครบทุกยี่ห้อ'}
              </h1>
              <div className="flex flex-row items-center justify-center gap-2 sm:gap-3 flex-wrap">
                <p
                  className="text-xs sm:text-sm md:text-lg lg:text-xl font-prompt text-white font-bold"
                  style={{
                    textShadow:
                      '2px 2px 4px rgba(0,0,0,0.8), -1px -1px 2px rgba(0,0,0,0.8), 1px -1px 2px rgba(0,0,0,0.8), -1px 1px 2px rgba(0,0,0,0.8)',
                  }}
                >
                  รถคุณภาพดี {Number.isFinite(totalCount) ? totalCount : 0} คัน พร้อมส่งมอบ
                </p>
                {safeTotalPages > 1 && (
                  <p
                    className="text-xs sm:text-xs md:text-sm lg:text-base font-prompt text-white font-semibold"
                    style={{
                      textShadow:
                        '2px 2px 4px rgba(0,0,0,0.8), -1px -1px 2px rgba(0,0,0,0.8), 1px -1px 2px rgba(0,0,0,0.8), -1px 1px 2px rgba(0,0,0,0.8)',
                    }}
                  >
                    หน้า {safePage}/{safeTotalPages}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="bg-white py-4 border-b border-gray-200 -mt-0">
        <div className="max-w-7xl mx-auto px-6">
          {/* ปุ่มย้อนกลับ */}
          <div className="mb-3">
            <button
              onClick={() => {
                if (window.history.length > 2) {
                  router?.back();
                } else {
                  router.push('/');
                }
              }}
              className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-primary transition-colors focus:outline-none group font-prompt"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-1.5 h-4 w-4 transform group-hover:-translate-x-1 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              ย้อนกลับ
            </button>
          </div>

          <nav
            aria-label="เส้นทางปัจจุบัน"
            className="flex items-center gap-2 text-sm text-gray-600 font-prompt"
          >
            <Link href="/" className="hover:text-primary transition-colors">
              หน้าแรก
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-primary font-medium" aria-current="page">
              รถทั้งหมด
            </span>
          </nav>

          <div className="mt-4 rounded-2xl border border-orange-500 bg-white px-4 py-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div className="text-sm text-gray-950 font-semibold font-prompt">
                รายการลงประกาศรถทั้งหมดของทางร้าน พร้อมข้อมูลติดต่อ
              </div>
              <div className="flex flex-wrap gap-2">
                <Link
                  href="/used-cars-chiang-mai"
                  className="inline-flex items-center justify-center rounded-xl bg-orange-700 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-800 active:scale-95 active:bg-orange-900 transition-all duration-200"
                >
                  ฝากขายราคาดี
                </Link>
                <Link
                  href="/ev-cars-chiang-mai"
                  className="inline-flex items-center justify-center rounded-xl bg-green-700 px-4 py-2 text-sm font-semibold text-white hover:bg-green-800 active:scale-95 transition-all duration-200"
                >
                  ⚡ รถ EV มือสอง
                </Link>
                <Link
                  href="/contact"
                  prefetch={false}
                  className="inline-flex items-center justify-center rounded-xl border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary hover:text-white active:scale-95 transition-all duration-200"
                >
                  นัดดูรถ / ติดต่อ
                </Link>
              </div>
            </div>

            <div className="mt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div className="text-xs text-gray-700 font-prompt">หรือเลือกดูตามยี่ห้อรถ:</div>
              <div className="flex flex-wrap gap-2">
                {[
                  { slug: 'toyota', label: 'Toyota' },
                  { slug: 'honda', label: 'Honda' },
                  { slug: 'isuzu', label: 'Isuzu' },
                  { slug: 'nissan', label: 'Nissan' },
                  { slug: 'mazda', label: 'Mazda' },
                  { slug: 'mitsubishi', label: 'Mitsubishi' },
                ].map(item => (
                  <Link
                    key={item.slug}
                    href={`/used-cars-chiang-mai-brand/${item.slug}`}
                    className="inline-flex items-center justify-center rounded-full border border-orange-400 bg-white px-3 py-1.5 text-xs font-semibold text-gray-800 hover:bg-orange-50 hover:border-orange-600 active:scale-95 active:bg-orange-100 transition-all duration-200"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cars Grid */}
      <section
        id="cars-grid-section"
        className="py-8 md:py-12 bg-white border-t border-gray-200"
        aria-label="รายการรถมือสองทั้งหมด"
      >
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-5 ipadpro:px-3 lg:px-6">
          {/* SEO Content: The Volume Up & Content Deep Strategy */}
          <div className="mb-8 p-6 md:p-8 bg-gradient-to-br from-orange-50 to-white rounded-2xl border border-orange-100 shadow-sm relative overflow-hidden">
            {/* Background accent for premium feel */}
            <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-orange-100/50 rounded-full blur-2xl pointer-events-none"></div>

            {/* SEO Header (Short version for UX) */}
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 font-prompt mb-3 relative z-10">
              ศูนย์รวมรถมือสองเชียงใหม่ คุณภาพดี คัดเกรดพรีเมียม
            </h2>
            <div className="text-sm md:text-base text-gray-600 font-prompt space-y-2 relative z-10 max-w-4xl">
              <p>
                เรามีรถคุณภาพพร้อมใช้งานให้เลือกชมมากกว่า{' '}
                <strong className="text-accent text-lg">
                  {Number.isFinite(totalCount) ? totalCount : 0} คัน
                </strong>{' '}
                ที่ครอบคลุมทุกไลฟ์สไตล์ รถทุกคันผ่านการตรวจเช็คสภาพ ไมล์แท้ โครงสร้างเดิม
                พร้อมบริการจัดไฟแนนซ์ อนุมัติไว ฟรีดาวน์ 0%
              </p>
            </div>
          </div>

          {!Number.isFinite(totalCount) || totalCount === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <h2 className="text-2xl font-bold text-gray-600 mb-2 font-prompt">
                ไม่พบรถมือสองเชียงใหม่ในหน้าต่างนี้
              </h2>
              <p className="text-gray-500 font-prompt">
                เรามีรถบ้านมือสองคุณภาพดีเข้ามาใหม่ทุกวัน
                ลองเปลี่ยนเงื่อนไขการค้นหาหรือติดต่อเราโดยตรง
              </p>
            </div>
          ) : (
            <>
              {/* จุดปักหมุดสำหรับด้านบนของรายการรถจริงๆ (ไม่รวม text SEO ด้านบน) */}
              <div id="cars-list-top" className="scroll-mt-24"></div>

              {/* Cards Grid - standardized layout, content-visibility added for performance */}
              <div className="car-grid grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4 lg:gap-4 xl:gap-6">
                {currentCars.map((car, index) => {
                  const isBelowFold = index >= 12;

                  if (isBelowFold && !showAllCars) {
                    return (
                      <div
                        key={`skeleton-${car.id}`}
                        ref={index === 12 ? observerRef : null}
                        className="h-[320px] w-full bg-gray-50 animate-pulse rounded-lg border border-gray-100"
                      ></div>
                    );
                  }

                  return (
                    <div
                      key={`wrapper-${car.id}`}
                      className={isBelowFold ? 'content-visibility-auto' : ''}
                      style={
                        isBelowFold
                          ? { contentVisibility: 'auto', containIntrinsicSize: '320px' }
                          : undefined
                      }
                    >
                      <CarCard
                        car={car}
                        liveStatus={liveStatuses?.[car.id]?.status}
                        priority={index < 2}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Pagination - Production Style (เหมือนเว็บไซต์จริง) */}
              {safeTotalPages > 1 && (
                <div className="mt-8 md:mt-12 flex flex-col items-center">
                  <nav
                    aria-label="การแบ่งหน้า"
                    className="flex items-center justify-center space-x-2"
                  >
                    {/* Previous Button */}
                    {currentPage > 1 ? (
                      <Link
                        href={getPageUrl(currentPage - 1)}
                        scroll={false}
                        className="px-3 py-2 text-sm font-medium rounded-lg border transition-colors bg-white border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center justify-center"
                        aria-label="ไปหน้าก่อนหน้า"
                        onClick={e => handlePaginationNav(currentPage - 1, e)}
                      >
                        ← ก่อนหน้า
                      </Link>
                    ) : (
                      <span
                        className="px-3 py-2 text-sm font-medium rounded-lg border transition-colors bg-gray-100 border-gray-200 text-gray-700 cursor-not-allowed flex items-center justify-center"
                        aria-hidden="true"
                      >
                        ← ก่อนหน้า
                      </span>
                    )}

                    {/* Page Numbers */}
                    <div className="flex items-center space-x-1">
                      {generatePageNumbers().map(page => (
                        <Link
                          key={page}
                          href={getPageUrl(page)}
                          scroll={false}
                          className={`w-10 h-10 text-sm font-medium rounded-lg border transition-colors flex items-center justify-center ${
                            page === currentPage
                              ? 'bg-primary border-primary text-white cursor-default'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                          aria-label={`ไปหน้าที่ ${page}`}
                          aria-current={page === currentPage ? 'page' : undefined}
                          onClick={e => handlePaginationNav(page, e)}
                        >
                          {page}
                        </Link>
                      ))}
                    </div>

                    {/* Next Button */}
                    {currentPage < safeTotalPages ? (
                      <Link
                        href={getPageUrl(currentPage + 1)}
                        scroll={false}
                        className="px-3 py-2 text-sm font-medium rounded-lg border transition-colors bg-white border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center justify-center"
                        aria-label="ไปหน้าถัดไป"
                        onClick={e => handlePaginationNav(currentPage + 1, e)}
                      >
                        ถัดไป →
                      </Link>
                    ) : (
                      <span
                        className="px-3 py-2 text-sm font-medium rounded-lg border transition-colors bg-gray-100 border-gray-200 text-gray-700 cursor-not-allowed flex items-center justify-center"
                        aria-hidden="true"
                      >
                        ถัดไป →
                      </span>
                    )}
                  </nav>

                  {/* Page Info */}
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600">
                      หน้า <span className="font-medium text-primary">{currentPage}</span> จาก{' '}
                      <span className="font-medium text-primary">{safeTotalPages}</span>
                    </p>
                  </div>
                </div>
              )}

              {/* SEO Long Text - Moved to bottom for better UX and conversion */}
              <section className="mt-12 md:mt-16 mb-8" aria-labelledby="seo-about-heading">
                <div className="rounded-2xl bg-blue-50/40 border border-blue-100 p-6 md:p-8">
                  <h2
                    id="seo-about-heading"
                    className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 font-prompt mb-4"
                  >
                    ทำไมต้องเลือกครูหนึ่งรถสวย ศูนย์รวมรถมือสองเชียงใหม่?
                  </h2>
                  <div className="text-sm md:text-base text-gray-700 font-prompt space-y-4 leading-[1.8]">
                    <p className="text-left">
                      ยินดีต้อนรับสู่{' '}
                      <strong className="text-primary font-semibold">ครูหนึ่งรถสวย</strong>{' '}
                      ศูนย์รวมรถมือสองเชียงใหม่ที่ได้รับความไว้วางใจจากลูกค้าทั่วภาคเหนือ
                      เราคัดสรรเฉพาะรถบ้านสภาพดี ไมล์แท้ ประวัติใสสะอาด โครงสร้างเดิม
                      ไม่เคยมีประวัติชนหนักหรือจมน้ำ เพื่อส่งมอบความมั่นใจสูงสุดให้กับคุณ
                    </p>
                    <p className="text-left">
                      ไม่ว่าคุณกำลังตามหา <strong>รถเก๋งประหยัดน้ำมัน</strong>{' '}
                      <span className="text-gray-500 text-sm">(Toyota, Honda, Mazda)</span>,{' '}
                      <strong>รถกระบะพันธุ์แกร่งทนทานสำหรับลุยงาน</strong>{' '}
                      <span className="text-gray-500 text-sm">(Isuzu, Nissan, Mitsubishi)</span>{' '}
                      หรือ<strong>รถอเนกประสงค์ SUV แบบครอบครัว</strong>
                    </p>
                    <p className="text-left">
                      รถยนต์มือสองทุกคันของเราผ่านการตรวจเช็คสภาพอย่างละเอียดจากช่างผู้เชี่ยวชาญ
                      พร้อมการรับประกันเครื่องยนต์และเกียร์หลังการขาย
                      เรามุ่งเน้นการให้บริการที่ซื่อสัตย์ โปร่งใส พร้อมให้คำปรึกษาด้านการจัดไฟแนนซ์
                    </p>
                    <p className="text-left">
                      <span className="inline-block bg-orange-100 text-orange-800 px-3 py-1.5 rounded-md font-semibold my-2 shadow-sm">
                        ออกรถได้ทุกอาชีพ จัดไฟแนนซ์ง่าย อนุมัติไว ฟรีดาวน์ 0%
                      </span>
                      <br />
                      และกรณีลูกค้าต่างจังหวัดเรามีบริการส่งรถฟรีถึงหน้าบ้านท่านทั่วประเทศไทย
                      หากคุณกำลังมองหารถยนต์ในราคาที่เหมาะสม คุ้มค่าเงินทุกบาท
                      ลองเลือกชมรถที่อัปเดตล่าสุดและติดต่อทีมงานของเราได้เลย
                    </p>
                  </div>
                </div>
              </section>

              {/* FAQ (AEO) - visible content to match FAQPage schema */}
              {!isFiltered && allCarsFaqs?.length > 0 && (
                <section className="mt-10 md:mt-14" aria-labelledby="faq-allcars-heading">
                  <div className="rounded-3xl border border-gray-200 bg-gray-50 p-4 sm:p-6">
                    <h2
                      id="faq-allcars-heading"
                      className="text-lg sm:text-xl font-bold text-gray-900 font-prompt"
                    >
                      คำถามที่พบบ่อย (FAQ)
                    </h2>
                    <p className="mt-1 text-sm text-gray-600 font-prompt">
                      คำตอบสั้นๆ ก่อนตัดสินใจดูรถและนัดหมาย
                    </p>

                    <div className="mt-4 space-y-3">
                      {allCarsFaqs.map(item => (
                        <details
                          key={item.q}
                          className="group rounded-2xl border border-gray-200 bg-white px-4 py-3"
                        >
                          <summary className="cursor-pointer list-none font-semibold text-gray-900 font-prompt flex items-start justify-between gap-3">
                            <span>{item.q}</span>
                            <span className="text-gray-400 group-open:rotate-180 transition-transform">
                              ▾
                            </span>
                          </summary>
                          <div className="mt-2 text-sm text-gray-700 font-prompt leading-relaxed">
                            {item.a}
                          </div>
                        </details>
                      ))}
                    </div>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

// SSR for all-cars to ensure Google sees correct catalog HTML for query params
// (pagination/filter/noindex/canonical) without relying on client-side JS.
export async function getServerSideProps(context) {
  // Server-only modules – kept out of the client bundle via require()
  const { SEO_KEYWORD_MAP } = require('../config/seo-keyword-map');
  const { ALL_CARS_FAQS, buildFaqPageJsonLd } = require('../lib/seo/faq.js');

  const enableServerTiming =
    process.env.NODE_ENV === 'development' || process.env.ENABLE_SERVER_TIMING === '1';

  const marks = [];
  const mark = name => {
    if (!enableServerTiming) return;
    marks.push({ name, at: Date.now() });
  };
  const duration = (start, end) => {
    if (!enableServerTiming) return null;
    const a = marks.find(m => m.name === start)?.at;
    const b = marks.find(m => m.name === end)?.at;
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    const d = b - a;
    return d >= 0 ? d : null;
  };

  mark('ssr:start');
  let cars = [];
  let shopifyError = null;
  try {
    // Run both fetches in parallel to reduce SSR latency (~500-800ms saved)
    mark('getAllCars:start');
    mark('readCarStatuses:start');
    let fetchError = null;
    const [result, carStatuses] = await Promise.all([
      getAllCars().catch(e => {
        fetchError = e;
        return [];
      }),
      readCarStatuses().catch(() => ({})),
    ]);
    mark('getAllCars:end');
    mark('readCarStatuses:end');
    if (fetchError) throw fetchError;
    cars = (Array.isArray(result) ? result : []).filter(c => !isEvCar(c));

    // ลดขนาดข้อมูลโดยเก็บเฉพาะฟิลด์ที่จำเป็น
    cars = cars.map(car => ({
      id: car.id,
      handle: car.handle,
      title: car.title,
      vendor: car.vendor,
      tags: car.tags,
      price: car.price,
      // Keep quick specs for CarCard (ปี/ไมล์/เกียร์/เชื้อเพลิง/ขับเคลื่อน)
      year: car.year ?? null,
      mileage: car.mileage ?? null,
      transmission: car.transmission ?? null,
      drivetrain: car.drivetrain || car.drive_type || car.wheel_drive || null,
      drive_type: car.drive_type || car.drivetrain || car.wheel_drive || null,
      installment: car.installment ?? null,
      fuelType: car.fuelType || car.fuel_type || null,
      fuel_type: car.fuel_type || car.fuelType || null,
      // Keep metaobject-backed display labels for cards (ประเภทรถ/ประเภทตัวถัง)
      // so mobile Safari doesn't depend on deferred client enrichment to show them.
      category: car.category ?? null,
      body_type: car.body_type ?? null,
      images: car.images?.slice(0, 1) || [], // เก็บแค่รูปแรกสำหรับ listing
      availableForSale: car.availableForSale,
      status: carStatuses[car.id]?.status || 'available', // Add status from file
    }));

    // แสดงรถทั้งหมดที่มีจริง ไม่จำกัดจำนวน
  } catch (error) {
    // Silent error handling for production - ให้ UI แสดงข้อมูลว่างแทน
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('getServerSideProps(/all-cars) error:', error);
      shopifyError = error?.message || 'unknown error';
    }
    cars = [];
  }

  mark('ssr:data:end');

  const q = context?.query || {};
  const initialSearchTerm = q.search ? normalizeQueryString(q.search, 120) : '';
  const initialPriceRange = q.price ? normalizePriceRange(q.price) : 'all';
  const initialBrandFilter = q.brand ? normalizeBrand(q.brand) : 'all';
  const initialPage = q.page ? normalizePageNumber(q.page) : 1;

  // Apply filtering/pagination on the server to reduce client hydration cost (TBT)
  const carsPerPage = 24;
  mark('ssr:filtering:start');
  let filtered = Array.isArray(cars) ? cars : [];

  // --- BEST PRACTICE SORTING FOR LARGE USED CAR WEBSITES ---
  // Push "Reserved" or "Sold" cars to the absolute bottom of the catalog.
  // This ensures buyers always see 'InStock' inventory at the very top.
  const { isReservedCar, isSoldCar } = require('../lib/carStatusUtils.js');
  filtered.sort((a, b) => {
    const aUnavailable = isReservedCar(a) || isSoldCar(a);
    const bUnavailable = isReservedCar(b) || isSoldCar(b);
    if (aUnavailable && !bUnavailable) return 1; // Push A down
    if (!aUnavailable && bUnavailable) return -1; // Push B down
    return 0; // Keep order
  });

  if (initialSearchTerm) {
    const term = String(initialSearchTerm).toLowerCase();
    filtered = filtered.filter(car => {
      const title = String(car?.title || '').toLowerCase();
      const vendor = String(car?.vendor || '').toLowerCase();
      const tags = Array.isArray(car?.tags) ? car.tags : [];
      return (
        title.includes(term) ||
        vendor.includes(term) ||
        tags.some(tag => {
          return String(tag || '')
            .toLowerCase()
            .includes(term);
        })
      );
    });
  }

  if (initialPriceRange !== 'all') {
    const [minRaw, maxRaw] = String(initialPriceRange).split('-');
    const min = Number(minRaw);
    const hasMax = typeof maxRaw !== 'undefined';
    const max = hasMax ? Number(maxRaw) : undefined;
    const validMin = Number.isFinite(min) && min >= 0;
    const validMax = !hasMax || (Number.isFinite(max) && max >= min);
    if (validMin && validMax) {
      filtered = filtered.filter(car => {
        const price = Number(car?.price?.amount ?? 0);
        if (!Number.isFinite(price)) return false;
        return hasMax ? price >= min && price <= max : price >= min;
      });
    }
  }

  if (initialBrandFilter !== 'all') {
    const bf = String(initialBrandFilter).toLowerCase();
    filtered = filtered.filter(car => {
      const title = String(car?.title || '').toLowerCase();
      const vendor = String(car?.vendor || '').toLowerCase();
      return title.includes(bf) || vendor.includes(bf);
    });
  }

  mark('ssr:filtering:end');

  const totalCount = filtered.length;
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / carsPerPage) : 1;
  const safePage = Math.min(Math.max(1, initialPage), totalPages);
  const startIndex = (safePage - 1) * carsPerPage;
  let pageCars = filtered.slice(startIndex, startIndex + carsPerPage);

  // Precompute JSON-LD for SEO on the server (lightweight) so the client doesn’t rebuild
  // a large graph during hydration. Skip on filtered pages because they are noindex.
  let structuredDataJson = null;
  try {
    const hasSearch = !!(initialSearchTerm && String(initialSearchTerm).trim());
    const hasPrice = initialPriceRange !== 'all';
    const hasBrand = initialBrandFilter !== 'all';
    // Free-text search creates unbounded URL variations -> noindex. Brand/price filters are a finite,
    // high-value set of long-tail landing pages and should stay indexable (kept in sync with the
    // client-side isFiltered logic below in this same file).
    const isFiltered = hasSearch;

    if (!isFiltered) {
      const seoParams = new URLSearchParams();
      if (hasPrice) seoParams.set('price', initialPriceRange);
      if (hasBrand) seoParams.set('brand', initialBrandFilter);
      if (safePage > 1) seoParams.set('page', String(safePage));
      const seoQs = seoParams.toString();
      const seoPath = seoQs ? `/all-cars?${seoQs}` : '/all-cars';

      // Build a unique name/description per brand+price combo so newly-indexable filtered pages
      // don't all share identical JSON-LD (avoids reinforcing duplicate-content signals).
      const ALL_CARS_BRAND_LABELS = {
        toyota: 'Toyota',
        honda: 'Honda',
        nissan: 'Nissan',
        mazda: 'Mazda',
        mitsubishi: 'Mitsubishi',
        isuzu: 'Isuzu',
        ford: 'Ford',
      };
      const ALL_CARS_PRICE_LABELS = {
        '0-100000': 'ราคาไม่เกิน 1 แสน',
        '100000-200000': 'ราคา 1-2 แสน',
        '200000-300000': 'ราคา 2-3 แสน',
        '300000-400000': 'ราคา 3-4 แสน',
        '400000-500000': 'ราคา 4-5 แสน',
        '500000-600000': 'ราคา 5-6 แสน',
        '600000-700000': 'ราคา 6-7 แสน',
        700000: 'ราคา 7 แสนขึ้นไป',
      };
      const filterNameParts = [];
      if (hasBrand && ALL_CARS_BRAND_LABELS[initialBrandFilter]) {
        filterNameParts.push(`รถ${ALL_CARS_BRAND_LABELS[initialBrandFilter]}`);
      } else {
        filterNameParts.push('รถมือสองทั้งหมด');
      }
      if (hasPrice && ALL_CARS_PRICE_LABELS[initialPriceRange]) {
        filterNameParts.push(ALL_CARS_PRICE_LABELS[initialPriceRange]);
      }
      const collectionName = filterNameParts.join(' ');

      const topCarImages = pageCars
        .slice(0, 4)
        .map(c => c?.images?.[0]?.url)
        .filter(Boolean)
        .map(url => (url.startsWith('/') ? 'https://www.chiangmaiusedcar.com' + url : url));
      const collectionSchema = {
        '@context': 'https://schema.org',
        image: topCarImages.length > 0 ? topCarImages : undefined,
        '@type': 'CollectionPage',
        name: `${collectionName}${totalPages > 1 ? ` - หน้า ${safePage}` : ''}`,
        description: `${collectionName} คุณภาพดี ${Number.isFinite(totalCount) ? totalCount : 0} คัน พร้อมส่งมอบ`,
        url: `https://www.chiangmaiusedcar.com${seoPath}`,
        mainEntity: {
          '@type': 'ItemList',
          name: 'รายการรถมือสอง',
          numberOfItems: pageCars.length,
          itemListElement: pageCars.map((car, index) => {
            const carUrl = car?.handle
              ? `https://www.chiangmaiusedcar.com/car/${car.handle}`
              : 'https://www.chiangmaiusedcar.com/all-cars';

            const imageUrls = car?.images?.length
              ? car.images
                  .slice(0, 3)
                  .map(img =>
                    img.url.startsWith('/') ? `https://www.chiangmaiusedcar.com${img.url}` : img.url
                  )
                  .filter(Boolean)
              : ['https://www.chiangmaiusedcar.com/herobanner/allusedcars.webp'];

            return {
              '@type': 'ListItem',
              position: startIndex + index + 1,
              item: {
                '@type': 'Car',
                '@id': carUrl,
                url: carUrl,
                name: car?.title || 'รถมือสอง',
                image: imageUrls,
                brand: car?.vendor ? { '@type': 'Brand', name: car.vendor } : undefined,
                modelDate: car?.year || undefined,
                vehicleTransmission: car?.transmission || undefined,
                mileageFromOdometer: car?.mileage
                  ? {
                      '@type': 'QuantitativeValue',
                      value:
                        typeof car.mileage === 'string'
                          ? Number(car.mileage.replace(/\\D/g, '')) || 0
                          : car.mileage,
                      unitCode: 'KMT',
                    }
                  : undefined,
                offers: {
                  '@type': 'Offer',
                  priceCurrency: 'THB',
                  price: car?.price?.amount || 0,
                  itemCondition: 'https://schema.org/UsedCondition',
                  availability: car?.availableForSale
                    ? 'https://schema.org/InStock'
                    : 'https://schema.org/OutOfStock',
                  url: carUrl,
                  seller: {
                    '@type': 'AutoDealer',
                    name: 'ครูหนึ่งรถสวย เชียงใหม่',
                  },
                },
              },
            };
          }),
        },
        publisher: {
          '@type': 'AutoDealer',
          name: 'ครูหนึ่งรถสวย',
          url: 'https://www.chiangmaiusedcar.com',
        },
      };

      const faq = buildFaqPageJsonLd({
        url: seoPath,
        faqs: ALL_CARS_FAQS,
      });
      const faqNode = { ...faq, '@type': 'FAQPage' };
      delete faqNode['@context'];

      structuredDataJson = JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [collectionSchema, faqNode],
      });
    }
  } catch {
    structuredDataJson = null;
  }

  mark('ssr:end');

  // Note: do not enrich specs during SSR.
  // Extra Shopify calls here significantly slow down pagination (page 1..n).
  // Client-side enrichment (deferred) fills any remaining spec gaps.

  // Cache hints: allow CDN to cache briefly while keeping inventory reasonably fresh
  if (context?.res?.setHeader) {
    context.res.setHeader('Cache-Control', 'public, s-maxage=900, stale-while-revalidate=3600');

    if (enableServerTiming) {
      const tGetAll = duration('getAllCars:start', 'getAllCars:end');
      const tStatuses = duration('readCarStatuses:start', 'readCarStatuses:end');
      const tFilter = duration('ssr:filtering:start', 'ssr:filtering:end');
      const tTotal = duration('ssr:start', 'ssr:end');

      const parts = [];
      if (Number.isFinite(tTotal)) parts.push(`ssr;dur=${tTotal}`);
      if (Number.isFinite(tGetAll)) parts.push(`shopify_allcars;dur=${tGetAll}`);
      if (Number.isFinite(tStatuses)) parts.push(`car_statuses;dur=${tStatuses}`);
      if (Number.isFinite(tFilter)) parts.push(`filtering;dur=${tFilter}`);

      if (parts.length > 0) {
        context.res.setHeader('Server-Timing', parts.join(', '));
      }
    }
  }

  return {
    props: {
      cars: pageCars,
      totalCount,
      totalPages,
      initialSearchTerm,
      initialPriceRange,
      initialBrandFilter,
      initialPage: safePage,
      structuredDataJson,
      shopifyError,
      seoAllCars: SEO_KEYWORD_MAP.allCars,
      allCarsFaqs: ALL_CARS_FAQS,
    },
  };
}
